/**
 * Korisnik ima SKUP rola, zapisan u jednoj `user.role` koloni kao zarezom odvojena lista
 * ("manager,veleprodaja"). Tako to očekuje i better-auth admin plugin: `createUser` i
 * `adminUpdateUser` primaju niz i sami ga spoje, a `has-permission` ga čita natrag preko
 * `role.split(",")`. Zato nema migracije — stari red `"admin"` je skup od jednog elementa.
 *
 * Ovo je jedino mjesto koje zna kako je rola zapisana; nigdje drugdje se sirovi `user.role`
 * ne uspoređuje sa stringom, jer bi `role === "admin"` tiho zakazao za "admin,veleprodaja".
 */

/** Hijerarhija prava nad WooCommerce trgovinama; korisnik ima najviše jednu. */
export const BASE_ROLES = ["admin", "manager", "viewer"] as const;
/** Modularni pristup koji se dodaje na baznu rolu; korisnik ih može imati više. */
export const ADDON_ROLES = ["veleprodaja", "dev"] as const;

export type BaseRole = (typeof BASE_ROLES)[number];
export type AddonRole = (typeof ADDON_ROLES)[number];
export type Role = BaseRole | AddonRole;

export const WHOLESALE_ROLE: AddonRole = "veleprodaja";
/** Vodi prijave (bugovi/prijedlozi) i dobija notifikacije o njima; maloprodaju ne otvara. */
export const DEV_ROLE: AddonRole = "dev";

const ALL_ROLES: readonly string[] = [...BASE_ROLES, ...ADDON_ROLES];

/** Korisnik iz sesije — i serverski `AuthContext` i klijentski `useSession`. */
type RoleCarrier = { role?: string | null } | null | undefined;
type RoleSource = string | RoleCarrier;

function rawRole(source: RoleSource): string {
  if (source == null) return "";
  if (typeof source === "string") return source;
  return source.role ?? "";
}

/**
 * Nepoznata rola se ignorira umjesto da ruši: nalog time gubi prava, ne dobiva ih.
 */
export function parseRoles(source: RoleSource): Role[] {
  const seen = new Set<string>();
  return rawRole(source)
    .split(",")
    .map((r) => r.trim())
    .filter((r) => {
      if (!ALL_ROLES.includes(r) || seen.has(r)) return false;
      seen.add(r);
      return true;
    }) as Role[];
}

export function serializeRoles(roles: readonly Role[]): string {
  return roles.join(",");
}

export function hasRole(source: RoleSource, role: Role): boolean {
  return parseRoles(source).includes(role);
}

/** Bazna rola korisnika, ili `null` kad nema nikakav pristup maloprodaji (komercijalista). */
export function baseRole(source: RoleSource): BaseRole | null {
  const roles = parseRoles(source);
  return BASE_ROLES.find((r) => roles.includes(r)) ?? null;
}

/**
 * Admin ima veleprodaju implicitno: i danas vidi pseudoshop u prebacivaču shopova i smije
 * raditi nad njim, pa bi doslovna provjera dodatka bila regresija.
 */
export function hasWholesale(source: RoleSource): boolean {
  return hasRole(source, WHOLESALE_ROLE) || baseRole(source) === "admin";
}

/** Ima li korisnik ikakav pristup WooCommerce trgovinama. */
export function hasRetail(source: RoleSource): boolean {
  return baseRole(source) !== null;
}

/**
 * Pregled i upravljanje prijavama. Admin ih je vodio i prije uvođenja `dev` role, pa ih
 * zadržava; `dev` ih vodi bez obzira na baznu rolu.
 */
export function canManageBugReports(source: RoleSource): boolean {
  return baseRole(source) === "admin" || hasRole(source, DEV_ROLE);
}

/**
 * Validacija skupa koji stiže od klijenta: poznate vrijednosti, bez duplikata, najviše
 * jedna bazna rola, i neprazan skup (nalog bez ijedne role ne bi mogao ništa).
 */
export function isValidRoleSet(roles: readonly string[]): boolean {
  if (roles.length === 0) return false;
  if (new Set(roles).size !== roles.length) return false;
  if (roles.some((r) => !ALL_ROLES.includes(r))) return false;
  return roles.filter((r) => (BASE_ROLES as readonly string[]).includes(r)).length <= 1;
}
